import React, { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import api, { describeAxiosError } from "@/lib/api";
import Brand from "@/components/Brand";
import PasswordInput from "@/components/PasswordInput";
import PasskeyLoginButton from "@/components/PasskeyLoginButton";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, Lightning, Info } from "@phosphor-icons/react";

export default function Login() {
    const { user, setUser } = useAuth();
    const nav = useNavigate();
    const [searchParams] = useSearchParams();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);
    // V2.0.36 — friendly banner explaining WHY the user landed on /login.
    // Bounced here by IdleLogout (idle) or the axios interceptor
    // (new_device single-session enforcement).
    const bounceReason = searchParams.get("reason");
    // V2.0.67 — when a partner (or anyone) clicks Sign in from the
    // /partner landing while already signed in as e.g. superadmin, we
    // must NOT bounce them to /superadmin. `?force=1` forces us to sign
    // the current user out and stay on the login form so they can
    // enter their partner credentials.
    const forceLogin = searchParams.get("force") === "1";

    const roleHome = (role) => {
        if (role === "superadmin") return "/superadmin";
        if (role === "partner") return "/partner/dashboard";
        return "/dashboard"; // admin, recruiter, hiring_manager
    };

    // Handle "already signed in" cases so users never get silently bounced
    // to the wrong dashboard when clicking Sign in from the header:
    //   • force=1 → always sign them out and let them enter fresh creds.
    //   • Same role (recruiter/admin/hiring_manager/partner) → send to /dashboard.
    //   • Wrong role (candidate) → sign them out so they can enter recruiter
    //     credentials here without confusion.
    useEffect(() => {
        if (!user) return;
        if (forceLogin || user.role === "candidate") {
            api.post("/auth/logout").catch(() => {});
            setUser(false);
            return;
        }
        nav(roleHome(user.role), { replace: true });
    }, [user, setUser, nav, forceLogin]);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            const { data } = await api.post("/auth/login", { email, password });
            if (data.role === "candidate") {
                setErr(
                    "This login is for recruiters and admins only. Candidates should use the candidate sign in page."
                );
                try {
                    await api.post("/auth/logout");
                } catch (_) {
                    /* ignore */
                }
                return;
            }
            setUser(data);
            nav(roleHome(data.role), { replace: true });
        } catch (e) {
            setErr(describeAxiosError(e));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen w-full flex bg-paper" data-testid="login-page">
            {/* Left: Form */}
            <div className="w-full lg:w-2/3 flex items-center justify-center px-6 sm:px-12 py-12 bg-white border-r border-[#E5E5E5]">
                <div className="w-full max-w-md">
                    <div className="mb-12">
                        <Brand size="h-9" />
                    </div>

                    <div
                        data-testid="login-role-badge"
                        className="inline-flex items-center gap-2 mb-5 bg-ink text-white px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em]"
                    >
                        <span className="inline-block w-1.5 h-1.5 bg-cobalt rounded-full" />
                        Recruiter & Admin sign in
                    </div>

                    <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mb-4">
                        Sign in to your <br />
                        <span className="text-cobalt">hiring desk.</span>
                    </h1>
                    <p className="text-[#525252] mb-4 max-w-sm">
                        India&apos;s smartest ATS — JDs, candidate kanban, AI scoring on every resume.
                    </p>
                    <p className="text-xs text-[#A3A3A3] mb-10">
                        Looking for the candidate portal?{" "}
                        <Link
                            data-testid="switch-to-candidate-login"
                            to="/candidate/login"
                            className="text-cobalt font-bold underline underline-offset-2 hover:text-cobalt-dark"
                        >
                            Candidate sign in →
                        </Link>
                    </p>

                    <form onSubmit={submit} className="space-y-4" data-testid="login-form">
                        {bounceReason && (
                            <div
                                data-testid="login-bounce-reason"
                                className="border border-cobalt/25 bg-cobalt/5 text-ink text-sm px-4 py-3 flex items-start gap-2 rounded"
                            >
                                <Info size={16} weight="bold" className="text-cobalt shrink-0 mt-0.5" />
                                <span>
                                    {bounceReason === "idle" && "You were signed out after 60 minutes of inactivity. Please log in again."}
                                    {bounceReason === "new_device" && "You signed in on another device. Please log in again to continue here."}
                                    {!["idle", "new_device"].includes(bounceReason) && "Your session ended. Please log in again."}
                                </span>
                            </div>
                        )}
                        <div>
                            <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                Email
                            </label>
                            <input
                                data-testid="login-email-input"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="w-full bg-white border border-[#E5E5E5] px-4 py-3 text-base text-ink placeholder-[#A3A3A3] focus:outline-none focus:border-cobalt focus:ring-1 focus:ring-cobalt"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                Password
                            </label>
                            <PasswordInput
                                data-testid="login-password-input"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                autoComplete="current-password"
                                className="w-full bg-white border border-[#E5E5E5] px-4 py-3 text-base text-ink placeholder-[#A3A3A3] focus:outline-none focus:border-cobalt focus:ring-1 focus:ring-cobalt"
                            />
                        </div>

                        {err && (
                            <div
                                data-testid="login-error"
                                className="border border-[#DC2626] bg-[#FEF2F2] text-[#DC2626] text-sm px-4 py-3"
                            >
                                {err}
                                {err.toLowerCase().includes("candidate") && (
                                    <>
                                        {" "}
                                        <Link
                                            to="/candidate/login"
                                            className="underline underline-offset-2 font-bold"
                                        >
                                            Go to candidate sign in →
                                        </Link>
                                    </>
                                )}
                            </div>
                        )}

                        <button
                            data-testid="login-submit-button"
                            disabled={loading}
                            type="submit"
                            className="btn-gradient w-full px-6 py-3.5 font-bold tracking-wide text-sm flex items-center justify-center gap-2"
                        >
                            {loading ? "Signing in…" : "Sign in"} <ArrowRight size={16} weight="bold" />
                        </button>

                        <div className="relative py-2">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-ink/10" />
                            </div>
                            <div className="relative flex justify-center">
                                <span className="bg-white px-3 text-[10px] font-black uppercase tracking-[0.2em] text-ink/40">Or</span>
                            </div>
                        </div>
                        <PasskeyLoginButton mode="recruiter" />

                        <div className="text-right">
                            <Link
                                to="/forgot-password"
                                data-testid="recruiter-forgot-password-link"
                                className="text-xs font-bold text-cobalt hover:text-cobalt-dark underline underline-offset-2"
                            >
                                Forgot password?
                            </Link>
                        </div>
                    </form>

                    <p className="mt-8 text-sm text-[#525252]">
                        Are you a candidate?{" "}
                        <Link
                            data-testid="goto-candidate-login-link"
                            to="/candidate/login"
                            className="text-cobalt font-bold underline underline-offset-4"
                        >
                            Candidate sign in
                        </Link>
                        {" "}·{" "}
                        <Link
                            data-testid="goto-candidate-signup-link"
                            to="/candidate/signup"
                            className="text-cobalt font-bold underline underline-offset-4"
                        >
                            Create account
                        </Link>
                    </p>
                    <p className="mt-2 text-xs text-[#A3A3A3]">
                        Recruiter? Accounts are invite-only — ask your admin to add you.
                    </p>

                    <div className="mt-10 pt-6 border-t border-[#E5E5E5] text-xs text-[#A3A3A3]">
                    </div>
                </div>
            </div>

            {/* Right: dark panel */}
            <div className="hidden lg:flex w-1/3 bg-ink text-white p-12 flex-col justify-between">
                <p className="text-xs font-bold uppercase tracking-[0.3em] text-white/40">
                    Built for Indian hiring teams
                </p>
                <div>
                    <p className="font-heading font-black text-6xl xl:text-7xl leading-[0.95] tracking-tighter">
                        NaukriDaddy, <br />
                        <span className="text-cobalt">sorted.</span>
                    </p>
                    <p className="mt-8 text-white/60 max-w-xs">
                        Every requirement, every candidate, scored by AI. One control room.
                    </p>
                </div>
                <div className="text-xs font-bold uppercase tracking-[0.3em] text-white/40">
                    © {new Date().getFullYear()} — NaukriDaddy.com
                </div>
            </div>
        </div>
    );
}
